// Helper function to get standard headers for JSON requests
// NOTE: Authentication is handled via HTTP-only cookies (HIPAA-compliant)
// Use credentials: 'include' in fetch calls to send cookies
const getAuthHeaders = () => {
  return {
    'Content-Type': 'application/json'
  };
};

// Product service for API calls without useQuery
export const productService = {
  // Get all products
  async getAllProducts(params?: { page?: number; limit?: number; category?: string; search?: string }) {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.category) queryParams.append('category', params.category);
      if (params?.search) queryParams.append('search', params.search);

      const url = `/api/products${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await fetch(url, {
        headers: getAuthHeaders(),
        credentials: 'include' // Send HTTP-only auth cookie
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch products: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching products:', error);
      throw error;
    }
  },

  // Get product by ID
  async getProductById(id: string) {
    try {
      const response = await fetch(`/api/products/${id}`, {
        headers: getAuthHeaders(),
        credentials: 'include' // Send HTTP-only auth cookie
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching product:', error);
      throw error;
    }
  },

  // Create new product
  async createProduct(productData: any) {
    try {
      // For now, we'll use a dummy manufacturer ID since we need to handle manufacturer selection
      // This should be updated based on the logged-in user's manufacturer profile
      const manufacturerId = '507f1f77bcf86cd799439011'; // Dummy ID - should be dynamic
      
      const response = await fetch(`/api/manufacturer/${manufacturerId}/products`, {
        method: 'POST',
        headers: getAuthHeaders(),
        credentials: 'include', // Send HTTP-only auth cookie
        body: JSON.stringify(productData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to create product: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating product:', error);
      throw error;
    }
  },

  // Update product
  async updateProduct(id: string, productData: any) {
    try {
      const response = await fetch(`/api/products/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        credentials: 'include', // Send HTTP-only auth cookie
        body: JSON.stringify(productData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to update product: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error updating product:', error);
      throw error;
    }
  },

  // Delete product
  async deleteProduct(id: string) {
    try {
      const response = await fetch(`/api/products/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
        credentials: 'include' // Send HTTP-only auth cookie
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Failed to delete product: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error deleting product:', error);
      throw error;
    }
  },

  // Get manufacturer products (for a specific manufacturer)
  async getManufacturerProducts(manufacturerId: string) {
    try {
      const response = await fetch(`/api/manufacturer/${manufacturerId}/products`, {
        headers: getAuthHeaders(),
        credentials: 'include' // Send HTTP-only auth cookie
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch manufacturer products: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching manufacturer products:', error);
      throw error;
    }
  }
};

export default productService;
